/*
  Save Button Component

  Button to save journey changes. Shows unsaved indicator and handles save flow.
*/

import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button, useSnackBar } from '@usealloy/component-library';
import { useAppSelector, useAppDispatch } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';
import {
  selectJourneyToken,
  selectHasUnsavedChanges,
  selectDocument,
  saveJourneyVersion,
} from '../store';
import { SaveVersionNotesModal } from './modals';

const SaveButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UnsavedIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
`;

const UnsavedDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
`;

const SaveButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const addSnackBar = useSnackBar();
  const { typhonDomain } = useContext(UserContext);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const journeyToken = useAppSelector(selectJourneyToken);
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);
  const document = useAppSelector(selectDocument);

  // Determine if this is a draft (matches saveThunk logic)
  const isDraft = document ? !document.active && !document.activated_at : false;

  const handleSaveClick = () => {
    // TODO: Add validation before opening modal
    setShowNotesModal(true);
  };

  const handleSaveConfirm = async (notes: string, setAsActive: boolean) => {
    if (!typhonDomain) {
      addSnackBar({
        title: 'Error: No typhon domain configured',
        type: 'danger',
      });
      return;
    }

    if (!journeyToken) {
      addSnackBar({
        title: 'Error: No journey token found',
        type: 'danger',
      });
      return;
    }

    setIsSaving(true);

    try {
      const result = await dispatch(saveJourneyVersion({
        notes,
        setAsActive,
        typhonDomain,
      })).unwrap();

      setShowNotesModal(false);

      let successMessage = '';
      if (setAsActive) {
        successMessage = 'Version saved and activated';
      } else if (isDraft) {
        successMessage = 'Draft saved';
      } else {
        successMessage = 'Version saved as draft';
      }

      addSnackBar({
        title: successMessage,
        type: 'success',
      });

      // Navigate to the newly saved version
      // This will cause useJourneyData to refetch with the new versionId
      const newVersionId = result.version_id;
      if (newVersionId) {
        navigate(`/v3/dashboard/policy/${journeyToken}/versions/${newVersionId}/`);
      }
    } catch (error: any) {
      addSnackBar({
        title: `Failed to save: ${error}`,
        type: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Determine button text based on state
  let buttonText = 'Save as Draft';
  if (isSaving) {
    buttonText = 'Saving...';
  } else if (isDraft) {
    buttonText = 'Save';
  }

  return (
    <>
      <SaveButtonContainer>
        {hasUnsavedChanges && (
          <UnsavedIndicator>
            <UnsavedDot />
            Unsaved changes
          </UnsavedIndicator>
        )}
        <Button
          type="button"
          onClick={handleSaveClick}
          disabled={!hasUnsavedChanges || isSaving}
        >
          {buttonText}
        </Button>
      </SaveButtonContainer>

      {showNotesModal && (
        <SaveVersionNotesModal
          isOpen={showNotesModal}
          onClose={() => setShowNotesModal(false)}
          onSave={handleSaveConfirm}
          isDraft={isDraft}
        />
      )}
    </>
  );
};

export default SaveButton;

